import useSpecialFixDecimals, { ISpecialFixDecimals } from '@/hooks/utilities/useSpecialFixDecimals';
import { createContext, useContext, useEffect, useState } from 'react';
import { useSession } from './LitSessionProvider';
import { QuoteStringGeneric, QuoteStringResponse, quoteString, validate } from '@/components/trade/swap-home/const';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { toSpecialFixed } from '@/lib/utils';
import buildAllPermit2Details from '@/lib/buildAllPermit2Details';
import { ChainId } from '@shared/utils/config/networks';
import { TDbOrder } from '@shared/types/db/orders';
import { MultichainSwapResult } from '@shared/types/actions';
import { useEnginePrice } from '@/hooks/QuotingEngineCalls/price/useEnginePrice';
import { executeOrderWrapper } from '@/lib/executorHelpers/executeOrder/executeOrderWrapper';
import { BasicTokenInfo, CovalentBalanceItem } from '@shared/types/frontend';
import { slackLogger } from '@shared/utils/off-chain/slack-logger';
import { IDLNTokenOrder, TEnginePriceWrapperResponse } from '@shared/utils/quoting-engine/transactions/genius-swap/types';
import { fetchSVMTokenBalances } from '@shared/utils/solana/base/fetchSVMTokenBalances';
import { fetchEVMTokenBalances } from '@shared/utils/evm/base/fetchEVMTokenBalance';
import { useMixpanel } from '@/lib/mixpanel';
import useGetWrappedWallet from '@/hooks/auth/useGetWrappedWallet';
import getFeesFromQuote from '@/lib/getFeesFromQuote';
import { useLocalStorage } from 'usehooks-ts';
import getToAssetPrices from '@/lib/trade/getToAssetPrices';
import getFromAssetPrices from '@/lib/trade/getFromAssetPrices';
import { useInterval } from '@/hooks/utilities/useInterval';
import { useRouter } from 'next/router';
type TabType = 'swap' | 'buy' | 'sell';
interface SwapContextInterface {
  selectFromAsset: boolean;
  setSelectFromAsset: (value: boolean) => void;
  selectToAsset: boolean;
  setSelectToAsset: (value: boolean) => void;
  toAssets: CovalentBalanceItem[];
  setToAssets: (value: Partial<CovalentBalanceItem>[]) => void;
  fromAssets: CovalentBalanceItem[];
  setFromAssets: (value: Partial<CovalentBalanceItem>[]) => void;
  timeUntilRefetchQuote: number;
  setTimeUntilRefetchQuote: (value: number) => void;
  swapLoading: boolean;
  setSwapLoading: (value: boolean) => void;
  swapResult: MultichainSwapResult | {
    error: string;
  };
  setSwapResult: (value: MultichainSwapResult | {
    error: string;
  }) => void;
  amount: string;
  setAmount: (value: string) => void;
  specialFixDecimals: ISpecialFixDecimals[];
  quoteData: TEnginePriceWrapperResponse | undefined;
  quoteLoad: boolean;
  quoteFetching: boolean;
  toAssetPriceData: BasicTokenInfo[];
  setToAssetPriceData: (value: BasicTokenInfo[]) => void;
  activeTab: TabType;
  setActiveTab: (value: TabType) => void;
  selectBuyAsset: boolean;
  setSelectBuyAsset: (value: boolean) => void;
  selectSellAsset: boolean;
  setSelectSellAsset: (value: boolean) => void;
  quoteStringInfo: QuoteStringGeneric<QuoteStringResponse>;
  swapWrapper: () => void;
  buyAmount: string;
  setBuyAmount: (value: string) => void;
  sellAmount: string;
  setSellAmount: (value: string) => void;
  fromConfig: {
    gas: number;
    slippage: number;
  };
  setFromConfig: (value: {
    gas: number;
    slippage: number;
  }) => void;
  toConfig: {
    gas: number;
    slippage: number;
  };
  setToConfig: (value: {
    gas: number;
    slippage: number;
  }) => void;
  buyMethod: string;
  setBuyMethod: (value: string) => void;
  priceFetching: boolean;
  useDLN: boolean;
  setUseDLN: (value: boolean) => void;
  isConfirmationDialogOpen: boolean;
  setIsConfirmationDialogOpen: (value: boolean) => void;
  quoteRefetch: () => void;
  setPriceFetching: (value: boolean) => void;
  resetAssets: () => void;
  triggerQuote: () => void;
}
const SwapContext = createContext({} as SwapContextInterface);
export const SwapProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    PKP,
    litNetwork,
    litSession,
    clients,
    ethersPKPWallet,
    decryptedWallets
  } = useSession();
  const {
    getEVMWallet,
    getSolanaWallet
  } = useGetWrappedWallet();
  const [selectFromAsset, setSelectFromAsset] = useState(false);
  const [selectToAsset, setSelectToAsset] = useState(false);
  const [isFirstRun, setIsFirstRun] = useState(true);
  const [fromAssets, setFromAssets] = useState<CovalentBalanceItem[]>([]);
  const [toAssets, setToAssets] = useState<CovalentBalanceItem[]>([]);
  const [timeUntilRefetchQuote, setTimeUntilRefetchQuote] = useState(10);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapResult, setSwapResult] = useState<MultichainSwapResult | {
    error: string;
    time?: number;
  }>({
    error: '',
    time: 0
  });
  const [activeTab, setActiveTab] = useState<TabType>('swap');
  const [selectBuyAsset, setSelectBuyAsset] = useState(false);
  const [amount, setAmount] = useState('0');
  const {
    specialFixDecimals
  } = useSpecialFixDecimals({
    assets: fromAssets
  });
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [priceFetching, setPriceFetching] = useState<boolean>(false);
  const [selectSellAsset, setSelectSellAsset] = useState(false);
  const [sellAmount, setSellAmount] = useState('0');
  const [buyAmount, setBuyAmount] = useState('0');
  const [buyMethod, setBuyMethod] = useState<'usd' | 'eth' | 'avax' | 'bnb' | 'sol'>('usd');
  const [fromConfig, setFromConfig] = useLocalStorage<{
    gas: number;
    slippage: number;
  }>('swap-from-config', {
    gas: 1,
    slippage: 1
  });
  const [toConfig, setToConfig] = useLocalStorage<{
    gas: number;
    slippage: number;
  }>('swap-to-config', {
    gas: 1,
    slippage: 1
  });
  const [useDLN, setUseDLN] = useState(true);
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const queryClient = new QueryClient();
  const {
    data: toAssetPriceData,
    isLoading: toAssetPriceDataLoad,
    isFetching: toAssetPriceDataFetching,
    refetch: toAssetPriceDataRefetch
  } = useQuery({
    queryKey: ['toAssetPriceData', toAssets],
    queryFn: async () => {
      return getToAssetPrices(toAssets);
    },
    enabled: toAssets.length > 0,
    refetchInterval: 5000
  });
  useEffect(() => {
    const usdAmount = fromAssets?.reduce((acc, curr) => {
      const usdAmount = curr.amountToUse;
      return acc + usdAmount;
    }, 0);
    setAmount(toSpecialFixed(usdAmount, 7).value);
  }, [fromAssets]);
  const {
    data: quoteData,
    isLoading: quoteLoad,
    isFetching: quoteFetching,
    refetch: quoteRefetch,
    handleSetParams,
    setParams,
    params
  } = useEnginePrice({
    useDLN
  });
  const router = useRouter();
  const urlHasTrade = router.asPath.includes('trade');
  const fromAssetsFromState = fromAssets;
  async function triggerQuote() {
    setPriceFetching(true);
    if (!validate(fromAssetsFromState, toAssets).valid) {
      setPriceFetching(false);
      console.log('new quote triggered from assets not valid', fromAssetsFromState, toAssets, validate(fromAssetsFromState, toAssets));
      return;
    }
    const fromAssets = await getFromAssetPrices({
      fromAssets: fromAssetsFromState
    });
    console.log('new quote triggered from assets', fromAssets, fromAssetsFromState);
    const tokensIn = await Promise.all(fromAssets.map(async x => {
      console.log('x.amountToUse', x.amountToUse);
      const percentage = x.amountToUse / x.usd_value;
      let amount;
      if (typeof x.amountToUse === 'number' && typeof x.formatted_balance === 'number' && x.formatted_balance !== 0) {
        const calculatedAmount = x.amountToUse / x.quote_rate / x.formatted_balance * parseFloat(x.balance);
        if (!isNaN(calculatedAmount)) {
          amount = BigInt(Math.round(calculatedAmount)).toString();
        } else {
          console.error('Calculated amount is NaN:', {
            x,
            calculatedAmount
          });
          amount = '0';
        }
      } else {
        console.error('Invalid input for amount calculation:', x);
        amount = '0';
      }
      console.log('percentagefrwrfe amount og', amount, PKP?.solanaAddress);
      if (percentage > 0.98) {
        let evmSVMBalances;
        if (x.network == ChainId.SOLANA) {
          evmSVMBalances = await fetchSVMTokenBalances({
            network: x.network,
            tokens: [x.contract_address],
            owner: PKP?.solanaAddress
          });
          console.log('percentagefrwrfe evmSVMBalances', evmSVMBalances, x);
        } else {
          evmSVMBalances = await fetchEVMTokenBalances({
            network: x.network,
            tokens: [x.contract_address],
            owner: PKP?.additionalEthAddress
          });
        }
        const rawBalance = evmSVMBalances[0].rawBalance;
        amount = rawBalance.toString();
      }
      const amountUsd = x.amountToUse;
      console.log('percentagefrwrfe ', percentage, amount, x.amountToUse, x.usd_value, amountUsd);
      return {
        address: x.contract_address,
        network: x.network,
        amount,
        amountUsd
      };
    }));
    const tokensOut = toAssets.map(x => {
      return {
        address: x.contract_address,
        network: x.network,
        percentage: x.percentage
      };
    });
    const tokensToApprove = tokensIn.map(x => {
      return {
        network: x.network,
        contract_address: x.address,
        amountToApprove: x.amount
      };
    });
    const {
      permitBatchDetails,
      permitSignatures
    } = await buildAllPermit2Details({
      tokensToApprove,
      ethersPKPWallet: await getEVMWallet()
    });
    let dlnTokenOrders: IDLNTokenOrder[] = [];
    const isNativeSwap = fromAssets?.[0]?.contract_address == 'So11111111111111111111111111111111111111112' && fromAssets?.[0]?.contract_name == 'Solana';
    if (tokensIn.length == 0 || tokensOut.length == 0) {
      //do nothing
    } else if (tokensIn.length == 1 && tokensOut.length == 1) {
      dlnTokenOrders = [{
        sourceChain: tokensIn[0].network,
        targetChain: tokensOut[0].network,
        tokenIn: tokensIn[0].address,
        tokenOut: tokensOut[0].address,
        tokenInAmount: tokensIn[0].amount,
        slippage: fromConfig.slippage,
        fromAddress: tokensIn[0].network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
        recipient: tokensOut[0].network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
        permitBatchDetail: permitBatchDetails[0],
        permitBatchSignature: permitSignatures[0],
        publicKeys: {
          payer: PKP?.solanaAddress
        },
        isNativeSwap,
        orchestrate: tokensIn[0].network == ChainId.SOLANA ? true : false
      }];
    } else if (tokensIn.length == 1 && tokensOut.length > 1) {
      dlnTokenOrders = tokensOut.map(x => {
        return {
          sourceChain: tokensIn[0].network,
          targetChain: x.network,
          tokenIn: tokensIn[0].address,
          tokenOut: x.address,
          tokenInAmount: BigInt(Math.floor(parseFloat(tokensIn[0].amount) * x.percentage)).toString(),
          slippage: fromConfig.slippage,
          fromAddress: tokensIn[0].network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
          recipient: x.network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
          permitBatchDetail: permitBatchDetails[0],
          permitBatchSignature: permitSignatures[0],
          publicKeys: {
            payer: PKP?.solanaAddress
          },
          isNativeSwap,
          orchestrate: tokensIn[0].network == ChainId.SOLANA ? true : false
        };
      });
    } else if (tokensIn.length > 1 && tokensOut.length == 1) {
      dlnTokenOrders = tokensIn.map((x, idx) => {
        return {
          sourceChain: x.network,
          targetChain: tokensOut[0].network,
          tokenIn: x.address,
          tokenOut: tokensOut[0].address,
          tokenInAmount: x.amount,
          slippage: fromConfig.slippage,
          fromAddress: x.network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
          recipient: tokensOut[0].network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
          permitBatchDetail: permitBatchDetails[idx],
          permitBatchSignature: permitSignatures[idx],
          publicKeys: {
            payer: PKP?.solanaAddress
          },
          isNativeSwap,
          orchestrate: tokensIn[0].network == ChainId.SOLANA ? true : false
        };
      });
    } else {
      const totalUsd = tokensIn.reduce((acc, curr) => acc + curr.amountUsd, 0);
      const sortedTokensInByUsd = tokensIn.sort((a, b) => a.amountUsd - b.amountUsd);
      let tokenInIndex = 0;
      let tokenInAmountUsdLeft = tokensIn[0].amountUsd;
      await Promise.all(tokensOut.map(async out => {
        const tokenIn = sortedTokensInByUsd[tokenInIndex];
        let requiredUsd = out.percentage / 100 * totalUsd;
        const {
          permitBatchDetails,
          permitSignatures
        } = await buildAllPermit2Details({
          tokensToApprove: [tokensToApprove[tokenInIndex]],
          ethersPKPWallet
        });
        while (requiredUsd > 0) {
          console.log('requiredUsd', requiredUsd, 'tokenInAmountUsdLeft', tokenInAmountUsdLeft, tokensIn, tokenInIndex, out.percentage, totalUsd);
          if (tokenInAmountUsdLeft < requiredUsd) {
            dlnTokenOrders.push({
              sourceChain: tokenIn.network,
              targetChain: out.network,
              tokenIn: tokenIn.address,
              tokenOut: out.address,
              tokenInAmount: BigInt(Math.floor(parseFloat(tokenIn.amount) * (tokenInAmountUsdLeft / tokenIn.amountUsd))).toString(),
              slippage: fromConfig.slippage,
              fromAddress: tokenIn.network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
              recipient: out.network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
              permitBatchDetail: permitBatchDetails[0],
              permitBatchSignature: permitSignatures[0],
              publicKeys: {
                payer: PKP?.solanaAddress
              },
              isNativeSwap,
              orchestrate: tokenIn.network == ChainId.SOLANA ? true : false
            });
            requiredUsd -= tokenInAmountUsdLeft;
            tokenInIndex++;
            tokenInAmountUsdLeft = sortedTokensInByUsd[tokenInIndex].amountUsd;
          } else {
            const percentageOfTokenInToUse = requiredUsd / tokenInAmountUsdLeft;
            dlnTokenOrders.push({
              sourceChain: tokenIn.network,
              targetChain: out.network,
              tokenIn: tokenIn.address,
              tokenOut: out.address,
              tokenInAmount: BigInt(Math.floor(parseFloat(tokenIn.amount) * percentageOfTokenInToUse)).toString(),
              slippage: fromConfig.slippage,
              fromAddress: tokenIn.network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
              recipient: out.network == ChainId.SOLANA ? PKP?.solanaAddress : PKP.additionalEthAddress,
              permitBatchDetail: permitBatchDetails[0],
              permitBatchSignature: permitSignatures[0],
              publicKeys: {
                payer: PKP?.solanaAddress
              },
              isNativeSwap,
              orchestrate: tokenIn.network == ChainId.SOLANA ? true : false
            });
            tokenInAmountUsdLeft -= requiredUsd;
            requiredUsd = 0;
          }
        }
      }));
    }
    console.log('new quote triggered from assets tokens in', tokensIn, 'tokensOut', tokensOut);
    const paramObj = {
      cloudWallet: PKP?.additionalEthAddress,
      solanaAddress: PKP?.solanaAddress,
      tokensIn,
      tokensOut,
      dlnTokenOrders,
      fromSlippage: isNaN(fromConfig.slippage) ? 1 : fromConfig.slippage,
      fromGasMultiplier: fromConfig.gas,
      toSlippage: toConfig.slippage,
      toGasMultiplier: toConfig.gas,
      permitBatchDetails,
      permitSignatures,
      publicKeys: {
        payer: PKP?.solanaAddress
      },
      refreshToken: [...Array(10)].map(() => Math.random().toString(36)[2]).join('')
    };
    console.log('new quote triggered from assets setting quote params to', paramObj, validate(fromAssets, toAssets));
    setParams(paramObj);
  }
  useEffect(() => {
    if (!quoteLoad && !quoteFetching) {
      setPriceFetching(false);
    }
  }, [quoteLoad, quoteFetching]);
  const quoteStringInfo = quoteString(quoteData, toAssetPriceData);
  async function swapWrapper() {
    setSwapLoading(true);
    try {
      const {
        sponsorFees,
        network,
        useOrchestrator
      } = getFeesFromQuote({
        quoteData
      });
      const orderType = quoteData?.quote?.result.aggregator == 'OKX Bridge' ? 'SWAP_OKX' : quoteData?.quote?.result.aggregator == 'DLN' ? 'SWAP_DLN' : quoteData?.quote?.evmInputTransactions || quoteData?.quote?.evmOutputTransactions ? 'SWAP_GENIUS' : 'SWAP_SINGLE';
      console.log('quoteData', quoteData, orderType);
      const modifiedAssets: CovalentBalanceItem & {
        amountToUse: number;
        amountToUseStr: string;
      }[] = fromAssets.map(obj => {
        const amount = obj.amountToUse;
        return {
          ...obj,
          amountToUse: amount / obj.quote_rate,
          amountToUseStr: (amount / obj.quote_rate)?.toFixed(3).toString()
        };
      });
      const executionData: {
        dbOrder: TDbOrder;
        dbOrderHistory: TDbOrder;
        result: MultichainSwapResult;
        error: string;
      } = await executeOrderWrapper({
        needsSponsor: Boolean(sponsorFees) || useOrchestrator,
        params: quoteData.quote,
        sessionSigs: litSession,
        cloudWallet: PKP?.additionalEthAddress,
        signer: await getEVMWallet(),
        keypair: await getSolanaWallet(),
        pkp: {
          ...PKP
        },
        baseUrl: '/api/wrapper?target=',
        environment: process.env.NEXT_PUBLIC_ENV,
        orderType: orderType,
        dbOrderParams: {
          tokenPrices: {
            tokensIn: modifiedAssets,
            tokensOut: toAssets
          },
          dbOrderType: selectBuyAsset ? 'MARKET_BUY' : selectSellAsset ? 'MARKET_SELL' : 'MARKET_SWAP',
          toAssetPriceData: toAssetPriceData
        }
      });
      queryClient.invalidateQueries({
        queryKey: ['userBalance']
      });
      console.log('executionData', executionData);
      const {
        dbOrder,
        dbOrderHistory,
        result,
        error
      } = executionData;
      console.log('Executing order final', result, result && result?.decodedResponse && result?.decodedResponse === '' || error, result && result?.decodedResponse?.error || !result?.decodedResponse || result?.error);
      if (result && result?.decodedResponse && result?.decodedResponse === '' || error) {
        console.error('Executing order swap error empty decodedResponse');
        setSwapResult({
          error: 'Error on execution data decodedResponse',
          time: 0
        });
        return;
      }
      if (result && result?.decodedResponse?.error || !result?.decodedResponse || result?.error) {
        await slackLogger({
          type: 'SWAP_ERROR',
          pkp: PKP,
          payload: {
            quote: quoteData,
            result: result
          }
        });
        setSwapResult({
          error: result?.decodedResponse.error || result?.error || 'Error Executing Order',
          time: 0
        });
        return;
      }
      LogToMixpanel({
        pkp: PKP,
        eventName: EVENTS.trade.finalizeTrade,
        category: CATEGORIES.trade,
        metaData: {
          fromAssets: fromAssets,
          toAssets: toAssets,
          amount: amount
        }
      });
      console.info('Should be succcess case...');
      if (result && result?.decodedResponse?.status === 'success') {
        console.info('SUCCESS CASE...');
        setSwapResult({
          ...result,
          order_id: dbOrder?.id,
          time: parseInt(result?.executionTime?.toString()) / 1000
        });
      }
    } catch (e) {
      console.log('swap error', e);
      setSwapResult({
        error: e,
        time: 0
      });
    } finally {
      setSwapLoading(false);
    }
  }
  const resetAssets = () => {
    setFromAssets([]);
    setToAssets([]);
    setSelectToAsset(false);
    setSelectFromAsset(false);
    setIsConfirmationDialogOpen(false);
    setSwapResult({
      error: '',
      time: 0
    });
  };
  useEffect(() => {
    let debounceTimeout: NodeJS.Timeout;
    if (fromAssets.length > 0 && toAssets.length > 0) {
      if (isFirstRun) {
        triggerQuote();
        setTimeUntilRefetchQuote(15);
        setIsFirstRun(false);
      } else {
        if (debounceTimeout) clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
          console.log('Executing quoteRefetch');
          triggerQuote();
          setTimeUntilRefetchQuote(15);
        }, 1200);
      }
    }
    return () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
    };
  }, [fromAssets, toAssets]);
  useInterval(() => {
    if (timeUntilRefetchQuote === 0) {
      triggerQuote();
      setTimeUntilRefetchQuote(15);
    } else if (quoteLoad || quoteFetching || priceFetching) {} else {
      setTimeUntilRefetchQuote(prev => prev - 1);
    }
  }, 1000, {
    enabled: !quoteLoad && !quoteFetching && !priceFetching && isConfirmationDialogOpen && validate(fromAssets, toAssets).valid && process.env.NEXT_PUBLIC_ENV !== 'LOCAL' && !quoteStringInfo?.message && urlHasTrade,
    immediate: false
  });
  return <SwapContext.Provider value={{
    selectFromAsset,
    setSelectFromAsset,
    selectToAsset,
    setSelectToAsset,
    toAssets,
    setToAssets,
    fromAssets,
    setFromAssets,
    timeUntilRefetchQuote,
    setTimeUntilRefetchQuote,
    swapLoading,
    setSwapLoading,
    swapResult,
    setSwapResult,
    amount,
    setAmount,
    specialFixDecimals,
    quoteData,
    quoteLoad,
    quoteFetching,
    toAssetPriceData,
    activeTab,
    setActiveTab,
    selectBuyAsset,
    setSelectBuyAsset,
    quoteStringInfo,
    swapWrapper,
    buyAmount,
    sellAmount,
    setSellAmount,
    setBuyAmount,
    setSelectSellAsset,
    selectSellAsset,
    fromConfig,
    setFromConfig,
    toConfig,
    setToConfig,
    buyMethod,
    priceFetching,
    useDLN,
    setUseDLN,
    isConfirmationDialogOpen,
    setIsConfirmationDialogOpen,
    quoteRefetch,
    setPriceFetching,
    resetAssets,
    triggerQuote
  }} data-sentry-element="unknown" data-sentry-component="SwapProvider" data-sentry-source-file="SwapProvider.tsx">
      {children}
    </SwapContext.Provider>;
};
export const useSwap = () => {
  const swapValues = useContext(SwapContext);
  return swapValues;
};